import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen,
  Copy,
  Film,
  Flame,
  Headphones,
  MessagesSquare,
  Mic,
  PenLine,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import AppLayout from "@/components/AppLayout";
import BilingualText from "@/components/BilingualText";
import Mascot from "@/components/Mascot";
import ReadinessMeter from "@/components/ReadinessMeter";
import { RecentBadges } from "@/components/BadgesGrid";
import { MASCOT } from "@/lib/mascot";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { loadGuestAttempts } from "@/lib/localProgress";
import { t } from "@/lib/i18n";
import { IDS } from "@/constants/testIds";

const SKILL_ROUTES = [
  { key: "reading", to: "/practice/reading", cn: "阅读", en: "Reading", icon: BookOpen, color: "from-sky-100 to-sky-50", accent: "text-sky-600", testid: IDS.dashboard.skillReading },
  { key: "vocabulary", to: "/practice/vocabulary", cn: "词汇", en: "Vocabulary", icon: Sparkles, color: "from-emerald-100 to-emerald-50", accent: "text-emerald-600", testid: IDS.dashboard.skillVocabulary },
  { key: "grammar", to: "/practice/grammar", cn: "语法", en: "Grammar", icon: Sparkles, color: "from-violet-100 to-violet-50", accent: "text-violet-600", testid: IDS.dashboard.skillGrammar },
  { key: "listening", to: "/practice/listening", cn: "听力", en: "Listening", icon: Headphones, color: "from-amber-100 to-amber-50", accent: "text-amber-600", testid: IDS.dashboard.skillListening },
  { key: "writing", to: "/writing", cn: "写作", en: "Writing", icon: PenLine, color: "from-fuchsia-100 to-fuchsia-50", accent: "text-fuchsia-600", testid: IDS.dashboard.skillWriting },
  { key: "speaking", to: "/speaking", cn: "口语", en: "Speaking", icon: Mic, color: "from-rose-100 to-rose-50", accent: "text-rose-600", testid: IDS.dashboard.skillSpeaking },
];

const SKILL_NAMES = {
  reading: { cn: "阅读", en: "Reading" },
  vocabulary: { cn: "词汇", en: "Vocabulary" },
  grammar: { cn: "语法", en: "Grammar" },
  listening: { cn: "听力", en: "Listening" },
  writing: { cn: "写作", en: "Writing" },
  speaking: { cn: "口语", en: "Speaking" },
};

export default function Dashboard() {
  const { user, guest, isAuthed, isGuest, currentProfile } = useAuth();
  const nav = useNavigate();
  const [progress, setProgress] = useState({
    readiness: 0, skill_avg: {}, weak_areas: [], total_attempts: 0, streak_days: 0,
  });
  const [badgeSummary, setBadgeSummary] = useState({ total: 0, earned_count: 0, latest: [] });

  useEffect(() => {
    if (!currentProfile) { nav("/"); return; }
    if (isAuthed) {
      api.get("/progress/summary").then(({ data }) => setProgress(data)).catch(() => {});
      api.get("/badges").then(({ data }) => setBadgeSummary(data.summary || { total: 0, earned_count: 0, latest: [] })).catch(() => {});
    } else if (isGuest) {
      const attempts = loadGuestAttempts();
      api.post("/progress/guest-summary", { attempts })
        .then(({ data }) => setProgress({ ...data, streak_days: 0 }))
        .catch(() => {});
      api.post("/badges/guest", { attempts })
        .then(({ data }) => setBadgeSummary(data.summary || { total: 0, earned_count: 0, latest: [] }))
        .catch(() => {});
    }
  }, [isAuthed, isGuest, currentProfile, nav]);

  const dailyGoal = 5;
  const todayCount = useMemo(() => {
    if (isGuest) {
      const attempts = loadGuestAttempts();
      const today = new Date().toISOString().slice(0, 10);
      return attempts.filter((a) => a.created_at?.slice(0, 10) === today).length;
    }
    return Math.min(dailyGoal, progress.total_attempts % (dailyGoal + 1));
  }, [isGuest, progress.total_attempts]);

  if (!currentProfile) return null;
  const isYounger = currentProfile.age_category === "7-9" || currentProfile.level === "A2";

  const copyCode = () => {
    if (user?.parent_link_code) {
      navigator.clipboard.writeText(user.parent_link_code);
      toast.success("Link code copied · 已复制");
    }
  };

  return (
    <AppLayout>
      <div data-testid={IDS.dashboard.root} className="space-y-10">
        {/* Header banner */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid md:grid-cols-12 gap-6"
        >
          <div className="md:col-span-8 card-plush p-6 sm:p-10 relative overflow-hidden">
            <span className="absolute -top-8 -right-8 w-48 h-48 rounded-full bg-amber-200/40 blur-2xl" />
            <span className="absolute -bottom-10 -left-10 w-56 h-56 rounded-full bg-sky-200/40 blur-2xl" />
            <div className="relative">
              <span className="badge-soft mb-3">
                {currentProfile.level || "—"} · {isYounger ? "A2 Key" : "B1 Preliminary"}
              </span>
              <BilingualText
                cn={`你好，${currentProfile.name || "同学"}！`}
                en={`Hello, ${currentProfile.name || "learner"}!`}
                size="hero"
              />
              <p className="mt-4 max-w-xl text-slate-600">
                <span className="cn block text-slate-800">
                  今天再练习几道题，就能离达标更近一步。
                </span>
                A few more questions today brings you closer to CEQ readiness.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  asChild
                  className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold shadow-md"
                >
                  <Link to="/practice/reading">
                    <span className="cn mr-2">开始今天的练习</span>Start today&apos;s session
                  </Link>
                </Button>
                {isGuest && (
                  <Button
                    variant="outline"
                    asChild
                    className="rounded-full font-bold"
                  >
                    <Link to="/auth?mode=register">
                      <span className="cn mr-2">注册以保存进度</span>Sign up to sync
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="md:col-span-4">
            <ReadinessMeter
              value={progress.readiness}
              subtitle={
                progress.total_attempts
                  ? `${progress.total_attempts} attempts · ${progress.total_attempts} 次练习`
                  : "Try a few questions to unlock your score"
              }
            />
          </div>
        </motion.section>

        {/* Widgets row */}
        <section className="grid md:grid-cols-3 gap-6">
          <Card data-testid={IDS.dashboard.streak} className="card-plush p-6 border-0">
            <div className="flex items-center justify-between">
              <BilingualText cn={t.streak.cn} en={t.streak.en} size="md" />
              <Flame className="w-6 h-6 text-orange-500" />
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="serif text-4xl font-black text-slate-900">
                {progress.streak_days || 0}
              </span>
              <span className="text-sm text-slate-500">days · 天</span>
            </div>
          </Card>

          <Card className="card-plush p-6 border-0">
            <div className="flex items-center justify-between">
              <BilingualText cn={t.daily_goal.cn} en={t.daily_goal.en} size="md" />
              <Target className="w-6 h-6 text-emerald-500" />
            </div>
            <div className="mt-3">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="serif text-4xl font-black text-slate-900">
                  {todayCount}
                </span>
                <span className="text-sm text-slate-500">/ {dailyGoal}</span>
              </div>
              <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-sky-400"
                  style={{ width: `${Math.min(100, (todayCount / dailyGoal) * 100)}%` }}
                />
              </div>
            </div>
          </Card>

          <Card data-testid={IDS.dashboard.weakAreas} className="card-plush p-6 border-0">
            <BilingualText cn={t.weak_areas.cn} en={t.weak_areas.en} size="md" />
            <div className="mt-3 flex flex-wrap gap-2">
              {(progress.weak_areas || []).length ? (
                progress.weak_areas.map((s) => (
                  <span
                    key={s}
                    className="badge-soft bg-rose-50 text-rose-700"
                  >
                    <span className="cn">{SKILL_NAMES[s]?.cn || s}</span>
                    <span>· {SKILL_NAMES[s]?.en || s}</span>
                  </span>
                ))
              ) : (
                <span className="text-sm text-slate-400">
                  <span className="cn">练习越多，越准确</span> · More data soon
                </span>
              )}
            </div>
          </Card>
        </section>

        {/* Prof. Scaly CTA — for all student accounts (tone adapts to level) */}
        {isAuthed && user?.role === "student" && (
          <section className="grid md:grid-cols-2 gap-5">
            <div className="relative overflow-hidden card-plush p-6 sm:p-8 bg-gradient-to-br from-emerald-50 via-white to-amber-50">
              <div className="flex items-center gap-5">
                <Mascot size="md" />
                <div className="flex-1 min-w-0">
                  <span className="badge-soft mb-2 bg-emerald-100 text-emerald-700">
                    {user?.level === "A2" ? "A2 · 7–9" : "B1 · 10–12"}
                  </span>
                  <BilingualText
                    cn={`向${MASCOT.cn}提问吧！`}
                    en={`Ask ${MASCOT.en} anything!`}
                    size="md"
                  />
                  <div className="mt-3">
                    <Button
                      asChild
                      className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
                      data-testid="dashboard-chat-cta"
                    >
                      <Link to="/chat">
                        <MessagesSquare className="w-4 h-4 mr-2" />
                        <span className="cn mr-1">开始聊天</span>Chat now
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden card-plush p-6 sm:p-8 bg-gradient-to-br from-sky-50 via-white to-fuchsia-50">
              <div className="flex items-center gap-5">
                <span className="w-16 h-16 rounded-2xl bg-white shadow grid place-items-center shrink-0">
                  <Film className="w-7 h-7 text-sky-600" />
                </span>
                <div className="flex-1 min-w-0">
                  <span className="badge-soft mb-2 bg-sky-100 text-sky-700">
                    New · 新
                  </span>
                  <BilingualText
                    cn="看小甲教授的视频课"
                    en="Watch Prof. Scaly's video lessons"
                    size="md"
                  />
                  <div className="mt-3">
                    <Button
                      asChild
                      className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
                      data-testid="dashboard-lessons-cta"
                    >
                      <Link to="/lessons">
                        <Film className="w-4 h-4 mr-2" />
                        <span className="cn mr-1">看课程</span>Watch lessons
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Recent badges strip */}
        {badgeSummary.latest?.length > 0 && (
          <section className="card-plush p-6">
            <div className="flex items-center justify-between mb-4">
              <BilingualText cn="最新徽章" en="Latest achievements" size="md" />
              <Link
                to="/progress"
                className="text-xs font-bold text-sky-600 hover:underline"
                data-testid="dashboard-view-all-badges"
              >
                <span className="cn mr-1">全部徽章</span>· {badgeSummary.earned_count}/{badgeSummary.total} unlocked →
              </Link>
            </div>
            <RecentBadges latest={badgeSummary.latest} />
          </section>
        )}

        {/* Skill cards */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <BilingualText cn="五项技能练习" en="Practise 5 skills" size="lg" />
            <Link
              to="/practice/reading"
              className="text-sm font-bold text-sky-600 hover:underline"
            >
              <span className="cn mr-1">继续上次</span>Continue →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SKILL_ROUTES.map((s, i) => {
              const avg = progress.skill_avg?.[s.key] || 0;
              return (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  whileHover={{ y: -3 }}
                >
                  <Link
                    to={s.to}
                    data-testid={s.testid}
                    className={`block card-plush p-6 hover:shadow-md transition-shadow`}
                  >
                    <div
                      className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${s.color} grid place-items-center mb-4`}
                    >
                      <s.icon className={`w-5 h-5 ${s.accent}`} />
                    </div>
                    <BilingualText cn={s.cn} en={s.en} size="md" />
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs text-slate-400 font-semibold uppercase tracking-widest">
                        avg · 平均
                      </span>
                      <span className="serif font-black text-slate-900">
                        {Math.round(avg)}
                      </span>
                    </div>
                    <div className="mt-2 w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full bg-slate-900`}
                        style={{ width: `${Math.min(100, avg)}%` }}
                      />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Student → Parent link code */}
        {isAuthed && user?.role === "student" && user?.parent_link_code && (
          <section className="card-plush p-6">
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-2xl bg-amber-100 grid place-items-center">
                <Users className="w-5 h-5 text-amber-700" />
              </span>
              <div className="flex-1">
                <BilingualText cn={t.your_link_code.cn} en={t.your_link_code.en} size="md" />
                <p className="text-sm text-slate-500 mt-1">
                  <span className="cn">把这个关联码给你的家长，他们就可以查看你的进度。</span>
                  <span className="block">Share this code with your parent so they can view your progress.</span>
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <code
                    data-testid={IDS.dashboard.linkCode}
                    className="px-4 py-2 rounded-full bg-slate-900 text-white font-bold tracking-widest"
                  >
                    {user.parent_link_code}
                  </code>
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={copyCode}
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>
            </div>
          </section>
        )}

        {isAuthed && user?.role === "parent" && (
          <div className="flex justify-end">
            <Button
              asChild
              className="rounded-full"
              data-testid={IDS.dashboard.parentView}
            >
              <Link to="/parent">
                <Users className="w-4 h-4 mr-2" />
                <span className="cn mr-1">家长面板</span>Parent dashboard
              </Link>
            </Button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
